import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { LoadingSpinner } from './components/LoadingSpinner';
import './i18n';

// Lazy load pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const TourPage = React.lazy(() => import('./pages/TourPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const SpaceDetailPage = React.lazy(() => import('./pages/SpaceDetailPage'));
const FloorListPage = React.lazy(() => import('./pages/FloorListPage'));
const FloorDetailPage = React.lazy(() => import('./pages/FloorDetailPage'));
const SUPPORTED_LANGS = ['es', 'en', 'zh'] as const;

const getPreferredLang = (lang?: string) => {
  const code = lang?.split('-')[0] || '';
  return SUPPORTED_LANGS.includes(code as (typeof SUPPORTED_LANGS)[number]) ? code : 'es';
};

// Language wrapper component
const LanguageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang as (typeof SUPPORTED_LANGS)[number])) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return <>{children}</>;
};

const LocalizedRedirect: React.FC<{ path?: string }> = ({ path = '' }) => {
  const { i18n } = useTranslation();
  const lang = getPreferredLang(i18n.language);
  return <Navigate to={`/${lang}${path}`} replace />;
};

const SpaceRedirect: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const lang = getPreferredLang(i18n.language);
  return <Navigate to={`/${lang}/spaces/${id}`} replace />;
};

const FloorRedirect: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const lang = getPreferredLang(i18n.language);
  return <Navigate to={`/${lang}/floors/${id}`} replace />;
};

const HashScrollHandler: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const targetId = decodeURIComponent(location.hash.slice(1));
    let attempts = 0;
    const maxAttempts = 10;

    const scrollToHash = () => {
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      if (attempts < maxAttempts) {
        attempts += 1;
        window.setTimeout(scrollToHash, 100);
      }
    };

    scrollToHash();
  }, [location.pathname, location.hash]);

  return null;
};

// Route wrapper for language prefixes
const AppRoutes: React.FC = () => {
  const { t } = useTranslation('common');

  return (
    <Suspense fallback={<LoadingSpinner size="lg" text={t('loading')} className="h-screen" />}>
      <HashScrollHandler />
      <Routes>
        {/* Language-prefixed routes */}
        <Route path="/:lang" element={<LanguageWrapper><HomePage /></LanguageWrapper>} />
        <Route path="/:lang/home" element={<LanguageWrapper><HomePage /></LanguageWrapper>} />
        <Route path="/:lang/tour" element={<LanguageWrapper><TourPage /></LanguageWrapper>} />
        <Route path="/:lang/contact" element={<LanguageWrapper><ContactPage /></LanguageWrapper>} />
        <Route path="/:lang/spaces/:id" element={<LanguageWrapper><SpaceDetailPage /></LanguageWrapper>} />
        <Route path="/:lang/floors" element={<LanguageWrapper><FloorListPage /></LanguageWrapper>} />
        <Route path="/:lang/floors/:id" element={<LanguageWrapper><FloorDetailPage /></LanguageWrapper>} />
        
        {/* Default routes (redirect to preferred language) */}
        <Route path="/" element={<LocalizedRedirect />} />
        <Route path="/home" element={<LocalizedRedirect path="/home" />} />
        <Route path="/tour" element={<LocalizedRedirect path="/tour" />} />
        <Route path="/contact" element={<LocalizedRedirect path="/contact" />} />
        <Route path="/spaces/:id" element={<SpaceRedirect />} />
        <Route path="/floors" element={<LocalizedRedirect path="/floors" />} />
        <Route path="/floors/:id" element={<FloorRedirect />} />
        
        {/* Fallback route */}
        <Route path="*" element={<LocalizedRedirect />} />
      </Routes>
    </Suspense>
  );
};

function App() {

  return (
    <Router>
      <div className="App min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          <AppRoutes />
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
